export interface LocationData {
  latitude: number;
  longitude: number;
  state?: string;
  region?: string;
  timestamp: number;
}

interface CachedLocationData extends LocationData {
  expiresAt: number;
}

const LOCATION_CACHE_KEY = 'onesignal_location';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const logWarn = (...args: unknown[]) => {
  try {
    // eslint-disable-next-line no-console
    console.warn('[Location]', ...args);
  } catch {}
};

export const requestLocationPermission =
  async (): Promise<LocationData | null> => {
    if (!navigator?.geolocation) {
      logWarn('Geolocation API not available');
      return null;
    }

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 3_600_000 // 1 hour
          });
        }
      );

      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now()
      };
    } catch (error) {
      logWarn('Failed to get location:', error);
      return null;
    }
  };

const saveLocationToCache = (data: LocationData): void => {
  try {
    if (!window.localStorage) {
      return;
    }

    const cacheData: CachedLocationData = {
      ...data,
      expiresAt: Date.now() + CACHE_DURATION_MS
    };

    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    logWarn('Failed to save location to cache:', error);
  }
};

const getLocationFromCache = (): LocationData | null => {
  try {
    if (!window.localStorage) {
      return null;
    }

    const cached = localStorage.getItem(LOCATION_CACHE_KEY);

    if (!cached) {
      return null;
    }

    const data: CachedLocationData = JSON.parse(cached);

    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    const { expiresAt: _expiresAt, ...locationData } = data;
    return locationData;
  } catch (error) {
    logWarn('Failed to get location from cache:', error);
    return null;
  }
};

export const clearLocationCache = (): void => {
  try {
    if (!window.localStorage) {
      return;
    }
    localStorage.removeItem(LOCATION_CACHE_KEY);
  } catch (error) {
    logWarn('Failed to clear location cache:', error);
  }
};

export const getLocationWithCache = async (): Promise<LocationData | null> => {
  const cachedLocation = getLocationFromCache();

  if (cachedLocation?.state && cachedLocation?.region) {
    return cachedLocation;
  }

  const newLocation = await requestLocationPermission();

  if (!newLocation) {
    return cachedLocation;
  }

  saveLocationToCache(newLocation);
  return newLocation;
};

export const updateLocationWithStateAndRegion = (
  location: LocationData,
  state: string,
  region: string
): void => {
  const updatedLocation: LocationData = {
    ...location,
    state,
    region
  };
  saveLocationToCache(updatedLocation);
};
